import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, ICampaignAttributes } from '@feathr/blackbox';
import { CampaignClass, CampaignState } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { Alert, Button, ConfirmModal, showError, TLDR, toast } from '@feathr/components';
import {
  CampaignContext,
  createCampaignStore,
} from '@feathr/extender/App/EventsPage/CampaignEditPage/CampaignEditPage.context';
import PixlPlusUpsell from '@feathr/extender/App/EventsPage/CampaignsPage/AddCampaignButton/PixlPlusUpsell';
import CampaignSummary from '@feathr/extender/App/EventsPage/CampaignSummary';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import EventSelect from '../EventSelect';

import * as styles from './CampaignDuplicateModal.css';

interface ICampaignDuplicateModalProps {
  campaign: Campaign;
  opened: boolean;
  close: () => void;
}

function CampaignDuplicateModal({
  campaign,
  opened,
  close,
}: Readonly<ICampaignDuplicateModalProps>): JSX.Element {
  const { isSegmentCampaign, isEmailMappingCampaign, isOmniCampaign, isFacebook } = campaign;
  const rootStore = useStore();
  const { Events, Targetings } = rootStore;
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const shouldShowUpsell =
    (isSegmentCampaign || isEmailMappingCampaign) && !isOmniCampaign && !isFacebook;

  const [selectedProject, setSelectedProject] = useState<ISelectOption>();
  const [modalStep, setModalStep] = useState<'duplicate' | 'upsell'>('duplicate');
  const [originalCampaignType, setOriginalCampaignType] = useState<CampaignClass | undefined>();
  const [isTryingPixlPlus, setIsTryingPixlPlus] = useState(false);

  const state = campaign.get('state');
  const isNotDraft = state !== CampaignState.Draft;
  const eventId = campaign.get('event');
  const event = eventId ? Events.get(eventId) : undefined;
  const eventName = event?.name ?? '';

  const isUpsellStep = modalStep === 'upsell';
  const isFirstStep = isUpsellStep || !shouldShowUpsell;
  const showStep2 =
    originalCampaignType &&
    (originalCampaignType === CampaignClass.Segment ||
      originalCampaignType === CampaignClass.EmailList);

  // Load targetings data for the campaign
  const targetings = Targetings.list({
    filters: { _parent: campaign.id, is_archived__ne: true },
  });

  const flightOrProjectURL = campaign.get('flight')
    ? localUrl(campaign.getItemUrl())
    : localUrl(`/projects/${campaign.get('parent')}/report`);

  // Initialize state when modal opens
  useEffect(() => {
    if (opened) {
      setOriginalCampaignType(campaign.get('_cls'));
      setModalStep(shouldShowUpsell ? 'upsell' : 'duplicate');
      setIsTryingPixlPlus(false);
      // Reset campaign to original state
      campaign.discardDirty();
    }
  }, [opened, campaign]);

  // Select the current event anytime the duplicate modal is triggered
  useEffect(() => {
    if (!selectedProject && event && !event.isPending) {
      setSelectedProject({ id: eventId, name: event.name });
    }
  }, [opened, eventId, event]);

  async function duplicate(): Promise<void> {
    try {
      const cloneOptions: Partial<ICampaignAttributes> = selectedProject
        ? { event: selectedProject.id }
        : {};

      // If trying Pixl Plus, clone as Omni
      if (isTryingPixlPlus) {
        cloneOptions.cls = CampaignClass.Omni;
      }

      await campaign.cloneCampaign(cloneOptions);

      toast(
        t(
          'Campaign "{{- name}}" is being duplicated to {{- eventName}}. We will email you when it is complete.',
          {
            name: campaign.name,
            eventName: selectedProject?.name ?? eventName,
          },
        ),
      );
      close();
    } catch (error: unknown) {
      showError(error, t);
    }
  }

  function handleClose(): void {
    // Reset campaign to original type before closing
    if (originalCampaignType) {
      campaign.set({ _cls: originalCampaignType });
    }
    campaign.discardDirty();
    close();
  }

  function handleBack(): void {
    campaign.discardDirty();
    setModalStep('upsell');
  }

  function handleChangeEvent(event: ISelectOption): void {
    setSelectedProject(event);
  }

  function handleTryPixlPlus(): void {
    setIsTryingPixlPlus(true);
    campaign.set({ _cls: CampaignClass.Omni });
    setModalStep('duplicate');
  }

  function handleContinueWithSelection(): void {
    setIsTryingPixlPlus(false);
    campaign.set({ _cls: originalCampaignType });
    setModalStep('duplicate');
  }

  const pixlPlusModalDescription = t(
    'Your campaign settings and content will duplicate exactly as expected, but with Pixl Plus you’ll get enhanced performance and expanded targeting options.',
  );

  const stepMap = {
    duplicate: {
      description: isTryingPixlPlus
        ? pixlPlusModalDescription
        : t('Are you sure you want to duplicate this campaign?'),
      cancelButtonText: isFirstStep && !campaign.isDirty ? t('Cancel') : t('Back'),
      confirmButtonText: isUpsellStep ? t('Duplicate with Pixl Plus') : t('Duplicate'),
      leftActions: showStep2
        ? t('Step {{current}} of {{total}}', { current: 2, total: 2 })
        : undefined,
      onClose: isFirstStep && !campaign.isDirty ? handleClose : handleBack,
      onConfirm: isUpsellStep ? handleTryPixlPlus : duplicate,
      size: 'md' as const,
      title: isTryingPixlPlus ? t('Duplicate as Pixl Plus Campaign') : t('Duplicate Campaign'),
    },
    upsell: {
      description: pixlPlusModalDescription,
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Try Pixl Plus'),
      leftActions: t('Step {{current}} of {{total}}', { current: 1, total: 2 }),
      onClose: handleClose,
      onConfirm: handleTryPixlPlus,
      size: 'default' as const,
      title: t('Duplicate as Pixl Plus Campaign'),
    },
  };

  const stepContent = stepMap[modalStep];

  return (
    <ConfirmModal
      cancelButtonText={stepContent.cancelButtonText}
      confirmButtonText={stepContent.confirmButtonText}
      confirmButtonType={isUpsellStep ? 'recommendation' : 'primary'}
      description={stepContent.description}
      leftActions={stepContent.leftActions}
      onClose={stepContent.onClose}
      onConfirm={stepContent.onConfirm}
      opened={opened}
      size={stepContent.size}
      t={t}
      title={stepContent.title}
    >
      <Stack>
        {isUpsellStep ? (
          <PixlPlusUpsell
            handleContinueWithSelection={handleContinueWithSelection}
            isMonetization={campaign.isMonetization}
            originalCampaignType={originalCampaignType ?? CampaignClass.Segment}
          />
        ) : (
          <>
            <Alert
              className={styles.alert}
              description={t("You will receive a notification once it's complete.")}
              title={t('Cloning this campaign will only take a few minutes. ')}
              type={'warning'}
            />
            {isNotDraft && (
              <TLDR
                action={<Button link={flightOrProjectURL}>{t('View report')}</Button>}
                description={t(
                  "You can easily recalculate conversions from the conversions table on this flight's report to include the newly cloned campaign.",
                )}
                name={'tldr-recalculate-conversions'}
                title={t('Recalculate conversions')}
              />
            )}

            <EventSelect
              helpText={t('This campaign will be duplicated to the selected project.')}
              id={eventId}
              label={t('Project')}
              onChange={handleChangeEvent}
              placeholder={t('Select a project...')}
              value={selectedProject}
            />

            {/* Ensure summary has access to the targetings so they don't load as empty */}
            <CampaignContext.Provider value={createCampaignStore({ targetings })}>
              <CampaignSummary
                campaign={campaign}
                className={styles.campaignSummary}
                layout={'vertical'}
              />
            </CampaignContext.Provider>
          </>
        )}
      </Stack>
    </ConfirmModal>
  );
}

export default observer(CampaignDuplicateModal);
